/**
 * La synthèse capacitaire : le besoin en face du disponible.
 *
 * *Demande du métier, 16/08/2026, sur le modèle de son classeur `VISUEL PLAN DE
 * CHARGE.xlsx` : « une partie MO et une partie matériel avec matériel par type, intercalée
 * entre les chantiers et le plan de charge ».*
 *
 * C'est le §4.7 du PRD, qui attendait : *« le besoin de 80 h face à X h disponibles »*. Le
 * besoin était calculé depuis le 15/08, la capacité en face ne l'était pas — et un besoin
 * sans capacité ne dit rien : 80 h est confortable à quinze conducteurs, intenable à un.
 *
 * ## Quatre lignes par code, celles du classeur
 *
 *  1. **référentiel salarié, interne** — heures NETTES, absences déjà déduites ;
 *  2. **référentiel salarié, externe** — l'intérim, compté à part ;
 *  3. **plan de charge** — ce que les contre-études validées demandent ;
 *  4. **taux d'utilisation** = charge ÷ (interne + externe).
 *
 * Le classeur écrit « NET (BRUT − maladie et congé) » : c'est `heures_disponibles` du
 * référentiel, jamais `heures_theoriques`. Une capacité brute afficherait un taux faux
 * dans le sens qui rassure — celui où l'on croit avoir du monde.
 *
 * ## Le matériel n'a pas de capacité au référentiel, et ça se voit
 *
 * Le parc ne porte ni congés ni arrêts (le module SAV est en V2) : sa capacité est donc
 * **le nombre de machines du type × 8 h**, la même base horaire que celle du référentiel
 * pour les hommes. C'est une hypothèse, pas une donnée, et l'écran l'écrit — un chiffre
 * calculé sur une convention doit dire laquelle, sinon il se lit comme une mesure.
 */
import { useMemo } from "react";
import type { JSX } from "react";
import { dec, formaterDateCourte, formaterNombre } from "@planif/moteur";
import type { Generique } from "@planif/moteur";
import type { BesoinDate, CapaciteJour, Materiel } from "./api";

/** Les heures ouvrées d'une machine dans une journée — la base du référentiel (PRD §7). */
export const HEURES_PAR_JOUR = 8;

/** Le seuil au-delà duquel la charge dépasse le disponible. */
const SEUIL_ROUGE = 1;
/** Le seuil de tension : au-delà, il n'y a plus de marge pour un aléa. */
const SEUIL_ORANGE = 0.8;

/**
 * La couleur d'un taux d'utilisation, telle que le classeur du métier la décrit.
 *
 * *« Si > à 100 % en rouge, si > à 80 % orange » (classeur, 16/08/2026), et « < 80 % en
 * vert » — confirmé par Chris le même jour.* Le classeur écrivait « orange » aux deux
 * lignes ; c'était une redite, et la question est tranchée : sous 80 %, il reste de la
 * marge et il n'y a rien à signaler.
 *
 * **100 % pile n'est pas un dépassement** : le classeur écrit « > à 100 % ». Une équipe
 * employée exactement à sa capacité est tendue, pas en faute — la passer en rouge
 * apprendrait à ignorer le rouge.
 */
export function couleurDuTaux(taux: number | null): string {
  if (taux === null) return "";
  if (taux > SEUIL_ROUGE) return "synthese__taux--rouge";
  return taux >= SEUIL_ORANGE ? "synthese__taux--orange" : "synthese__taux--vert";
}

/* ---------------------------------------------------------------------------
 * Le calcul
 * ------------------------------------------------------------------------- */

export type LigneSynthese = {
  readonly code: string;
  readonly libelle: string;
  /** Par jour : les trois grandeurs qui composent la lecture. */
  readonly parJour: ReadonlyMap<
    string,
    { readonly interne: string; readonly externe: string; readonly charge: string }
  >;
};

/**
 * Les lignes de la partie main-d'œuvre.
 *
 * **Un code apparaît dès qu'il a une charge OU une capacité**, jamais seulement l'une
 * des deux : un code chargé sans personne disponible est le cas le plus grave — et il
 * disparaîtrait d'un tableau construit sur la seule capacité. Un code disponible sans
 * charge est l'inverse : de la ressource qui ne sert pas.
 */
export function syntheseMO(
  besoins: readonly BesoinDate[],
  capacite: readonly CapaciteJour[],
  generiques: readonly Generique[],
): readonly LigneSynthese[] {
  const libelles = new Map(
    generiques.filter((g) => g.type === "MO").map((g) => [g.code, g.libelle]),
  );
  const lignes = new Map<string, Map<string, { interne: string; externe: string; charge: string }>>();

  const cellule = (
    code: string,
    date: string,
  ): { interne: string; externe: string; charge: string } => {
    const parJour = lignes.get(code) ?? new Map();
    const existante = parJour.get(date) ?? { interne: "0", externe: "0", charge: "0" };
    parJour.set(date, existante);
    lignes.set(code, parJour);
    return existante;
  };

  for (const ligne of capacite) {
    const c = cellule(ligne.codeMo, ligne.date);
    c.interne = dec(c.interne).add(dec(ligne.heuresInterne)).toFixed();
    c.externe = dec(c.externe).add(dec(ligne.heuresExterne)).toFixed();
  }

  for (const besoin of besoins) {
    // Les livraisons ne consomment personne : quarante tonnes de grave n'occupent aucun
    // conducteur. Les mêler ferait entrer des tonnes dans un total d'heures.
    if (besoin.heures === null || besoin.estMateriel) continue;
    const c = cellule(besoin.code, besoin.date);
    c.charge = dec(c.charge).add(dec(besoin.heures)).toFixed();
  }

  return [...lignes.entries()]
    .map(([code, parJour]) => ({
      code,
      libelle: libelles.get(code) ?? "",
      parJour,
    }))
    .sort((a, b) => a.code.localeCompare(b.code, "fr"));
}

/**
 * Les lignes de la partie matériel, par type de machine.
 *
 * Le parc est rattaché au chiffrage par son **code générique** (`04-03-15-101`), et c'est
 * cette maille qui compte les machines interchangeables — deux pelles du même code se
 * remplacent, deux « PELLE » de codes différents pas forcément. Le libellé affiché est
 * celui du référentiel des génériques : il est stable, là où le `type` d'une fiche varie
 * d'une saisie à l'autre.
 */
export function syntheseMateriel(
  besoins: readonly BesoinDate[],
  materiels: readonly Materiel[],
  generiques: readonly Generique[],
  jours: readonly string[],
): readonly LigneSynthese[] {
  const libelles = new Map(
    generiques.filter((g) => g.type === "MAT").map((g) => [g.code, g.libelle]),
  );

  const parc = new Map<string, number>();
  for (const machine of materiels) {
    if (machine.generique === null || machine.generique === "") continue;
    parc.set(machine.generique, (parc.get(machine.generique) ?? 0) + 1);
  }

  const charges = new Map<string, Map<string, string>>();
  for (const besoin of besoins) {
    if (besoin.heures === null || !besoin.estMateriel) continue;
    const parJour = charges.get(besoin.code) ?? new Map<string, string>();
    parJour.set(besoin.date, dec(parJour.get(besoin.date) ?? "0").add(dec(besoin.heures)).toFixed());
    charges.set(besoin.code, parJour);
  }

  // Seuls les codes CHARGÉS sont rendus. C'est la différence avec la main-d'œuvre : le
  // parc compte 43 codes génériques, et afficher les quarante lignes vides d'un chantier
  // qui en emploie trois noierait les trois qui comptent. Le parc dormant est une
  // question d'immobilisation, pas de plan de charge.
  return [...charges.entries()]
    .map(([code, parJour]) => {
      const machines = parc.get(code) ?? 0;
      const disponible = dec(machines).mul(dec(HEURES_PAR_JOUR)).toFixed();
      const complet = new Map<string, { interne: string; externe: string; charge: string }>();
      for (const jour of jours) {
        const charge = parJour.get(jour);
        if (charge === undefined && machines === 0) continue;
        complet.set(jour, {
          // Le parc entier est « interne » : rien n'est loué dans le référentiel, et
          // inventer une colonne de location afficherait toujours zéro.
          interne: disponible,
          externe: "0",
          charge: charge ?? "0",
        });
      }
      return { code, libelle: libelles.get(code) ?? "", parJour: complet };
    })
    .sort((a, b) => a.code.localeCompare(b.code, "fr"));
}

/** Le taux d'utilisation d'une cellule, ou `null` quand il n'y a aucune capacité. */
export function tauxDe(cellule: {
  interne: string;
  externe: string;
  charge: string;
}): number | null {
  const disponible = Number(cellule.interne) + Number(cellule.externe);
  if (disponible <= 0) return null;
  return Number(cellule.charge) / disponible;
}

/* ---------------------------------------------------------------------------
 * Le rendu
 * ------------------------------------------------------------------------- */

export function SyntheseCapacite({
  besoins,
  capacite,
  materiels,
  generiques,
  jours,
  erreurCapacite,
}: {
  besoins: readonly BesoinDate[];
  capacite: readonly CapaciteJour[];
  materiels: readonly Materiel[];
  generiques: readonly Generique[];
  jours: readonly string[];
  /** Non nul = le référentiel n'a pas répondu. Le besoin reste affiché, sans le face-à-face. */
  erreurCapacite: string | null;
}): JSX.Element {
  const mo = useMemo(
    () => syntheseMO(besoins, capacite, generiques),
    [besoins, capacite, generiques],
  );
  const materiel = useMemo(
    () => syntheseMateriel(besoins, materiels, generiques, jours),
    [besoins, materiels, generiques, jours],
  );

  return (
    <>
      <h3>Synthèse — besoin et disponible</h3>
      <p className="etat">
        Par code, quatre lignes : ce dont on dispose <strong>en interne</strong>, ce qu'on
        ajoute <strong>en intérim</strong>, ce que le plan de charge demande, et le taux
        d'utilisation. Les heures disponibles sont <strong>nettes</strong> — congés et
        arrêts déjà déduits par le référentiel.
        {erreurCapacite !== null && (
          <>
            {" "}
            <span className="en-ecart">
              Capacité indisponible : {erreurCapacite} Le besoin ci-dessous reste juste ;
              c'est le face-à-face qui manque.
            </span>
          </>
        )}
      </p>

      <BlocSynthese titre="Main-d'œuvre" lignes={mo} jours={jours} />

      <p className="note">
        Matériel : le disponible est le <strong>nombre de machines du parc rattachées au
        code × {HEURES_PAR_JOUR} h</strong>. C'est une convention, pas une mesure — le parc
        ne porte ni panne ni immobilisation tant que le module SAV n'existe pas (V2).
      </p>
      <BlocSynthese titre="Matériel" lignes={materiel} jours={jours} />
    </>
  );
}

function BlocSynthese({
  titre,
  lignes,
  jours,
}: {
  titre: string;
  lignes: readonly LigneSynthese[];
  jours: readonly string[];
}): JSX.Element {
  if (lignes.length === 0) {
    return (
      <p className="etat">
        {titre} : rien à confronter sur cette période — ni charge, ni capacité.
      </p>
    );
  }

  return (
    <div className="plan__defilement">
      <table className="plan__charge synthese">
        <thead>
          <tr>
            <th className="plan__entete-chantier">{titre}</th>
            <th className="synthese__nature" />
            {jours.map((jour) => (
              <th key={jour} className="plan__jour">
                {formaterDateCourte(jour)}
              </th>
            ))}
          </tr>
        </thead>
        {lignes.map((ligne) => (
          <tbody key={ligne.code} className="synthese__groupe">
            {(
              [
                ["Référentiel", "interne"],
                ["Référentiel", "externe"],
                ["Plan de charge", "charge"],
                ["Tx d'utilisation", "taux"],
              ] as const
            ).map(([intitule, quoi]) => (
              <tr key={quoi} className={quoi === "taux" ? "synthese__ligne-taux" : undefined}>
                {quoi === "interne" && (
                  <th className="plan__entete-chantier" rowSpan={4} scope="rowgroup">
                    <span className="code">{ligne.code}</span>
                    {ligne.libelle === "" ? null : <span className="note"> {ligne.libelle}</span>}
                  </th>
                )}
                <td className="synthese__nature">
                  {intitule}
                  {quoi === "interne" && <span className="note"> interne</span>}
                  {quoi === "externe" && <span className="note"> externe</span>}
                </td>
                {jours.map((jour) => {
                  const cellule = ligne.parJour.get(jour);
                  if (cellule === undefined) return <td key={jour} className="plan__vide" />;
                  if (quoi === "taux") {
                    const taux = tauxDe(cellule);
                    return (
                      <td key={jour} className={`synthese__taux ${couleurDuTaux(taux)}`}>
                        {taux === null
                          ? // Une charge sans personne disponible : le pire cas, et il ne
                            // doit surtout pas s'afficher « 0 % ».
                            Number(cellule.charge) > 0
                            ? "aucun"
                            : ""
                          : `${formaterNombre(String(taux * 100), 0)} %`}
                      </td>
                    );
                  }
                  const valeur = cellule[quoi];
                  return (
                    <td key={jour} className={Number(valeur) === 0 ? "plan__vide" : undefined}>
                      {Number(valeur) === 0 ? "" : formaterNombre(valeur, 0)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        ))}
      </table>
    </div>
  );
}
